/**
 * IBacktrackr.ts - IBacktrackr - Standard interface for undo/redo functionality
 * 
 * CHANGE HISTORY:
 * - 251021_0452 [97a0397] danielbeer: fix: correct automated type annotations and test scripts
 * - 251020_0732 [95fb3a1] danielbeer: fix: complete test infrastructure - all tests passing
 * - 251020_0617 [9601b37] danielbeer: feat: implement interface-based architecture with enforcement
 */
export interface IBacktrackr {
  /**
   * Record a change for undo/redo
   * @param change - Change record
   */
  recordChange(change: ChangeRecord): void;

  /**
   * Undo the last change
   * @returns boolean True if undo was performed
   */
  undo(): boolean;

  /**
   * Redo the last undone change
   * @returns boolean True if redo was performed
   */
  redo(): boolean;

  /**
   * Check if undo is available
   * @returns boolean True if can undo
   */
  canUndo(): boolean;

  /**
   * Check if redo is available
   * @returns boolean True if can redo
   */
  canRedo(): boolean;

  /**
   * Clear undo/redo history
   */
  clear(): void;

  /**
   * Get undo history
   * @returns ChangeRecord[] Array of changes
   */
  getHistory(): ChangeRecord[];
}

export interface ChangeRecord {
  type: 'create' | 'update' | 'delete';
  entityType?: string;
  itemId?: string;
  data?: any;
  before?: any;
  after?: any;
  timestamp?: number;
  [key: string]: any;
}

/**
 * Type guard to check if an object implements IBacktrackr
 */
export function isBacktrackr(obj): obj is IBacktrackr {
  return (
    obj !== null &&
    obj !== undefined &&
    typeof obj === 'object' &&
    typeof obj.recordChange === 'function' &&
    typeof obj.undo === 'function' &&
    typeof obj.redo === 'function' &&
    typeof obj.canUndo === 'function' &&
    typeof obj.canRedo === 'function' &&
    typeof obj.clear === 'function' &&
    typeof obj.getHistory === 'function'
  );
}
